
import { defineComponent, PropType } from "vue";
import * as fb from "../../../firebase";
import { generateDateKey } from "@/mixins/tools";
import { UpdateSubscriptionNoVolumeRequest } from "functions/src/Subscriptions/SubscriptionsInterfaces";
import {
  ACADEMY_SUBSCRIPTION_IDS,
  hasAnySubscriptionOf,
  maxRemainingDaysOf,
} from "functions/src/Subscriptions/packageIds";

type NoVolumeKind = "both" | "prize";

export default defineComponent({
  name: "activate-gtr-basic",
  props: {
    username: { type: String, required: true },
    email: { type: String, required: true },
    // Map<subId, remainingDays> from Admin-SearchUser (numbers, NOT objects).
    subscriptions: {
      type: Object as PropType<Map<string, number>>,
      default: () => new Map(),
    },
  },
  emits: ["userActivated", "error"],
  computed: {
    // `subscriptions` es un Map<doc-id, díasRestantes>, y la familia de academia
    // tiene DOS doc-ids desde el renombre aditivo (`trading-academy` ∪
    // `gtr-fly`). Las dos preguntas de abajo son DISTINTAS y leen la familia
    // entera, cada una a su manera:
    //
    //   · «está activo» = VIGENCIA. Los días de la generación que más lejos
    //     llegue. Leer sólo `trading-academy` en un usuario traducido devuelve
    //     los días del documento VIEJO, que está vencido, y la herramienta lo
    //     enseña como inactivo teniendo `gtr-fly` al día.
    //   · «ya ha estado activo» = EXISTENCIA, no vigencia: un documento vencido
    //     sigue existiendo. El Premio es sólo para estrenos (el backend revienta
    //     si no, GTRAcademy.ts:1058-1060), y quien compró `gtr-fly` de cero no
    //     tiene documento viejo ninguno — preguntando sólo por el viejo, la
    //     herramienta le habilita un botón que el backend le va a rechazar.
    basicActive(): boolean {
      const remainingDays = maxRemainingDaysOf(
        this.subscriptions,
        ACADEMY_SUBSCRIPTION_IDS
      );
      return typeof remainingDays === "number" && remainingDays >= 0;
    },
    everActivated(): boolean {
      return hasAnySubscriptionOf(this.subscriptions, ACADEMY_SUBSCRIPTION_IDS);
    },
  },
  data() {
    return {
      enabled: false,
      // null when idle, else which button is in-flight (drives its spinner).
      activating: null as NoVolumeKind | null,
    };
  },
  methods: {
    activate(kind: NoVolumeKind) {
      if (this.activating !== null) return;
      this.activating = kind;
      const ref =
        kind === "prize" ? "noVolumeActivationPrize@" : "noVolumeActivation@";
      const subscriptionRequest: UpdateSubscriptionNoVolumeRequest = {
        subscriptionId: kind,
        duration: 0,
        durationUnit: "days",
        username: this.username.toLowerCase(),
        email: this.email,
        isDirectAffiliate: false,
        noVolume: true,
        noVolumeRef: ref + generateDateKey(),
      };
      const UpdateSubscription = fb.func.httpsCallable(
        "Admin-ActivateSubscriptionWithoutVolume"
      );
      UpdateSubscription(subscriptionRequest)
        .then((response) => {
          if (response.data.OPCODE == "SUCCESS") {
            this.$emit("userActivated");
          } else if (response.data.OPCODE == "ERROR") {
            this.$emit(
              "error",
              "Error al Activar el Usuario",
              response.data.message +
                "\n" +
                (response.data.messageDetails || "")
            );
          }
        })
        .finally(() => {
          this.activating = null;
        });
    },
  },
});
